/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react';
import {
  Badge, Button, Spinner, Table,
} from 'react-bootstrap';
import { useAccountsList } from '../../../Hooks/useAccountsCache';
import { isRefundable, refundedOriginalIds } from '../../../Utils/paymentStatus';
import { formatDateTime } from '../../../Utils/timezone';
import { formatPaymentAmount, PaymentStatusBadge } from '../../Payments/Components/paymentDisplay';
import paymentBookingAllocations from '../../Payments/Components/paymentBookingAllocations';
import EditAllocationModal from '../../Payments/Components/EditAllocationModal';
import RecordWirePaymentModal from './RecordWirePaymentModal';
import RefundPaymentModal from './RefundPaymentModal';

/**
 * This booking's share of a payment split across several bookings, with the
 * whole split for the tooltip — or null when the payment is the booking's
 * alone.
 */
function splitShare(payment, bookingId) {
  const allocations = paymentBookingAllocations(payment);
  if (allocations.length < 2) return null;
  const mine = allocations.find((a) => a.booking_id === String(bookingId));
  if (!mine) return null;
  return {
    amount: mine.gross_amount,
    title: `Split across ${allocations.map((a) => `${a.booking_reference || a.booking_id} (${formatPaymentAmount(a.gross_amount, payment.currency)})`).join(', ')}`,
  };
}

/**
 * Every Payment row attached to a booking (lines[].booking_id or the
 * counterparty), with Refund on each refundable one, Edit (which order
 * line(s) it pays) on a refundable payment from the booking, and a way to
 * record a wire — "Mark Wire Received" until an inbound payment is recorded,
 * "Record another payment" after. Order Details' Financial tab.
 *
 * `payments` is the booking's useBookingPayments state, owned by the caller
 * so it can read the list too; `onRecorded(payment)`,
 * `onRefunded(offset, originalId, { original, wasFullRefund })` and
 * `onReallocated(payment)` run after the list has been updated.
 */
function PaymentHistory({
  booking, payments: state, onRecorded = () => {}, onRefunded = () => {},
  onReallocated = () => {},
}) {
  const { payments, loading, error } = state;
  const [showWire, setShowWire] = useState(false);
  const [refunding, setRefunding] = useState(null);
  const [reallocating, setReallocating] = useState(null);
  const { byId: accountsById } = useAccountsList();
  const refundedIds = refundedOriginalIds(payments);
  const hasInboundCompleted = payments.some((p) => p?.direction === 'in' && p?.status === 'completed');

  let body;
  if (loading) {
    body = (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
        <span className="ms-2">Loading payments...</span>
      </div>
    );
  } else if (error) {
    body = (
      <div className="text-danger small mb-2">
        <i className="fa fa-exclamation-triangle me-1" />
        {error}
      </div>
    );
  } else if (payments.length === 0) {
    body = <p className="text-muted small mb-3">No payments recorded yet.</p>;
  } else {
    body = (
      <Table responsive size="sm" hover className="mb-3">
        <thead className="table-light">
          <tr>
            <th>Date</th>
            <th>Account</th>
            <th>Direction</th>
            <th className="text-end">Gross</th>
            <th className="text-end">Fee</th>
            <th className="text-end">Net</th>
            <th>Status</th>
            <th>Reference</th>
            <th>Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => {
            const isOffset = !!p?.parent_payment_id;
            const account = p.account_id ? accountsById.get(p.account_id) : null;
            const share = splitShare(p, booking?._id);
            return (
              <tr key={p._id} className={isOffset ? 'table-light' : undefined}>
                <td>
                  {isOffset && (
                    <span className="me-1 text-muted" title="Refund offset of an earlier payment">
                      ↪
                    </span>
                  )}
                  {p.occurred_at ? formatDateTime(p.occurred_at) : '-'}
                </td>
                <td>{account ? account.name : '-'}</td>
                <td>
                  <Badge bg={p.direction === 'in' ? 'success' : 'secondary'}>
                    {p.direction || '-'}
                  </Badge>
                </td>
                <td className="text-end">
                  {formatPaymentAmount(p.gross_amount, p.currency)}
                  {share && (
                    <div className="small text-muted text-nowrap" title={share.title} data-testid="payment-share">
                      {`${formatPaymentAmount(share.amount, p.currency)} for this booking`}
                    </div>
                  )}
                </td>
                <td className="text-end">{formatPaymentAmount(p.fee, p.currency)}</td>
                <td className="text-end fw-bold">{formatPaymentAmount(p.net_amount, p.currency)}</td>
                <td><PaymentStatusBadge status={p.status} /></td>
                <td><small className="text-muted">{p.external_refs?.bank_reference || '-'}</small></td>
                <td><small className="text-muted">{p.notes || '-'}</small></td>
                <td className="text-nowrap">
                  {isRefundable(p, refundedIds) && p.counterparty?.kind === 'booking' && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="me-1"
                      onClick={() => setReallocating(p)}
                      title="Edit which order line(s) this payment is allocated to"
                    >
                      <i className="fa fa-pencil me-1" />
                      Edit
                    </Button>
                  )}
                  {isRefundable(p, refundedIds) && (
                    <Button size="sm" variant="outline-danger" onClick={() => setRefunding(p)}>
                      Refund
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  return (
    <>
      {body}
      {/* A second payment is ordinary — a deposit, then the balance — so
          recording one never goes away; it only stops being the call to
          action once something inbound is in. */}
      <Button
        variant={hasInboundCompleted ? 'outline-primary' : 'primary'}
        size="sm"
        onClick={() => setShowWire(true)}
      >
        <i className="fa fa-university me-1" />
        {hasInboundCompleted ? 'Record another payment' : 'Mark Wire Received'}
      </Button>

      <RecordWirePaymentModal
        show={showWire}
        onHide={() => setShowWire(false)}
        booking={booking}
        onPaymentRecorded={(payment) => {
          state.recorded(payment);
          onRecorded(payment);
        }}
      />

      <RefundPaymentModal
        show={!!refunding}
        onHide={() => setRefunding(null)}
        payment={refunding || {}}
        onRefunded={(offset, originalId) => {
          onRefunded(offset, originalId, state.refunded(offset, originalId));
        }}
      />

      <EditAllocationModal
        show={!!reallocating}
        onHide={() => setReallocating(null)}
        payment={reallocating || {}}
        onEdited={(updated) => {
          state.replaced(updated);
          setReallocating(null);
          onReallocated(updated);
        }}
      />
    </>
  );
}

export default PaymentHistory;
